/**
 * API Service Layer
 * Handles all API calls to the PHP backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Submit worker form
 * @param {FormData} formData Form data with worker submission
 * @returns {Promise} API response
 */
export const submitWorkerForm = async (formData) => {
  const response = await fetch(`${API_BASE_URL}/submit.php`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Submission failed');
  }

  return data;
};

/**
 * Admin login
 * @param {string} username Username
 * @param {string} password Password
 * @returns {Promise} API response
 */
export const login = async (username, password) => {
  const response = await fetch(`${API_BASE_URL}/login.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ username, password })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Login failed');
  }

  return data;
};

/**
 * Admin logout
 * @returns {Promise} API response
 */
export const logout = async () => {
  const response = await fetch(`${API_BASE_URL}/logout.php`, {
    method: 'POST',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Logout failed');
  }

  return data;
};

/**
 * Get dashboard statistics
 * @returns {Promise} API response with stats
 */
export const getStats = async () => {
  const response = await fetch(`${API_BASE_URL}/get-stats.php`, {
    method: 'GET',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch statistics');
  }

  return data;
};

/**
 * Get submissions with filters
 * @param {Object} params Query parameters
 * @returns {Promise} API response with submissions
 */
export const getSubmissions = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_BASE_URL}/get-submissions.php${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch submissions');
  }

  return data;
};

/**
 * Get submission details
 * @param {number} id Submission ID
 * @returns {Promise} API response with submission details
 */
export const getSubmissionDetails = async (id) => {
  const response = await fetch(`${API_BASE_URL}/get-submission-details.php?id=${id}`, {
    method: 'GET',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch submission details');
  }

  return data;
};

/**
 * Delete submission
 * @param {number} id Submission ID
 * @returns {Promise} API response
 */
export const deleteSubmission = async (id) => {
  const response = await fetch(`${API_BASE_URL}/delete.php?id=${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete submission');
  }

  return data;
};

/**
 * OPTIMIZATION: Batch delete submissions
 * Deletes multiple submissions in a single transaction
 * @param {Array<number>} ids Array of submission IDs to delete
 * @returns {Promise} API response
 */
export const batchDeleteSubmissions = async (ids) => {
  const response = await fetch(`${API_BASE_URL}/batch-delete.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ ids })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to batch delete submissions');
  }

  return data;
};

/**
 * Get download URL for a file
 * @param {number} fileId File ID
 * @param {boolean} view If true, returns URL for inline viewing (default: false)
 * @returns {string} Download URL
 */
export const getDownloadUrl = (fileId, view = false) => {
  const viewParam = view ? '&view=true' : '';
  return `${API_BASE_URL}/download.php?file_id=${fileId}${viewParam}`;
};

/**
 * Get list of available documents
 * @returns {Promise} API response with documents list
 */
export const getDocuments = async () => {
  const response = await fetch(`${API_BASE_URL}/documents.php`, {
    method: 'GET'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch documents');
  }

  return data;
};

/**
 * Get download URL for a document
 * @param {string} filename Document filename
 * @returns {string} Download URL
 */
export const getDocumentDownloadUrl = (filename) => {
  return `${API_BASE_URL}/documents.php?download=${encodeURIComponent(filename)}`;
};

/**
 * Get all documents (Admin)
 * @returns {Promise} API response with documents list
 */
export const getAdminDocuments = async () => {
  const response = await fetch(`${API_BASE_URL}/manage-documents.php`, {
    method: 'GET',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch documents');
  }

  return data;
};

/**
 * Update Google Docs link for a document (Admin)
 * @param {number} documentId Document ID
 * @param {string} googleDocsLink Google Docs/Sheets link
 * @returns {Promise} API response
 */
export const updateDocumentLink = async (documentId, googleDocsLink) => {
  const response = await fetch(`${API_BASE_URL}/manage-documents.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      id: documentId,
      google_docs_link: googleDocsLink
    })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update document link');
  }

  return data;
};

/**
 * Add new document template (Admin)
 * @param {FormData} formData Form data with title, pdf_file, google_docs_link, file_type
 * @returns {Promise} API response
 */
export const addDocument = async (formData) => {
  const response = await fetch(`${API_BASE_URL}/manage-documents.php`, {
    method: 'POST',
    body: formData,
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to add document');
  }

  return data;
};

/**
 * Delete document template (Admin)
 * @param {number} documentId Document ID
 * @returns {Promise} API response
 */
export const deleteDocument = async (documentId) => {
  const response = await fetch(`${API_BASE_URL}/manage-documents.php`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ id: documentId })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete document');
  }

  return data;
};

/**
 * Upload filled form (Word/Excel)
 * @param {FormData} formData - Form data including worker details and filled form file
 * @returns {Promise} API response
 */
export const uploadFilledForm = async (formData) => {
  const response = await fetch(`${API_BASE_URL}/upload-filled-form.php`, {
    method: 'POST',
    body: formData,
    credentials: 'include' // Include session cookies
  });

  // Get response text first to handle parse errors
  const text = await response.text();

  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    console.error('JSON Parse Error:', e);
    console.error('Response text:', text);
    throw new Error('Invalid server response. Please try again.');
  }

  if (!response.ok) {
    throw new Error(data.error || 'Upload failed');
  }

  return data;
};

/**
 * Worker login
 * @param {string} username Username
 * @param {string} password Password
 * @returns {Promise} API response
 */
export const workerLogin = async (username, password) => {
  const response = await fetch(`${API_BASE_URL}/worker-login.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ username, password })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Login failed');
  }

  return data;
};

/**
 * Worker logout
 * @returns {Promise} API response
 */
export const workerLogout = async () => {
  const response = await fetch(`${API_BASE_URL}/worker-logout.php`, {
    method: 'POST',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Logout failed');
  }

  return data;
};

/**
 * Get worker's own submissions
 * @param {Object} params Query parameters
 * @returns {Promise} API response with submissions
 */
export const getMySubmissions = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_BASE_URL}/worker-my-submissions.php${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch submissions');
  }

  return data;
};

/**
 * Get all workers (Admin only)
 * @param {Object} params Query parameters
 * @returns {Promise} API response with workers list
 */
export const getWorkers = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_BASE_URL}/admin-workers.php${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include'
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch workers');
  }

  return data;
};

/**
 * Create new worker (Admin only)
 * @param {Object} workerData Worker information
 * @returns {Promise} API response
 */
export const createWorker = async (workerData) => {
  const response = await fetch(`${API_BASE_URL}/admin-workers.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(workerData)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create worker');
  }

  return data;
};

/**
 * Update worker (Admin only)
 * @param {Object} workerData Worker information including id
 * @returns {Promise} API response
 */
export const updateWorker = async (workerData) => {
  const response = await fetch(`${API_BASE_URL}/admin-workers.php`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(workerData)
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update worker');
  }

  return data;
};

/**
 * Delete worker (Admin only)
 * @param {number} workerId Worker ID
 * @returns {Promise} API response
 */
export const deleteWorker = async (workerId) => {
  const response = await fetch(`${API_BASE_URL}/admin-workers.php`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ id: workerId })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete worker');
  }

  return data;
};

/**
 * Convert Word/Excel file to PDF and open in new window
 * @param {number} fileId File ID to convert
 * @returns {Window} Opens PDF in new browser window/tab
 */
export const convertToPDF = (fileId) => {
  const url = `${API_BASE_URL}/convert-to-pdf.php?file_id=${fileId}`;
  const pdfWindow = window.open(url, '_blank');

  if (!pdfWindow) {
    throw new Error('Pop-up blocked. Please allow pop-ups to view PDF.');
  }

  return pdfWindow;
};
